import { useCallback } from "react";
import { lang } from "../lang";

// Hook for handling data import from JSON file
export function useDataImport(taskDispatch, todoDispatch, importData) {
  const handleImportData = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) {
        console.warn("No file selected for import");
        alert("Please select a file to import.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          if (!e.target.result) {
            throw new Error("File is empty");
          }
          const data = JSON.parse(e.target.result);
          if (process.env.NODE_ENV === "development") {
            console.log("Parsed JSON data:", data);
          }
          // Validate structure
          if (!data || typeof data !== "object") {
            throw new Error("Invalid JSON: Expected an object");
          }
          const requiredFields = ["tasks", "todoLists", "settings"];
          const missingFields = requiredFields.filter(
            (field) => !(field in data)
          );
          if (missingFields.length > 0) {
            throw new Error(
              `Invalid JSON structure: Missing required fields: ${missingFields.join(
                ", "
              )}`
            );
          }
          if (!Array.isArray(data.tasks)) {
            throw new Error("Invalid JSON: tasks must be an array");
          }
          if (!Array.isArray(data.todoLists)) {
            throw new Error("Invalid JSON: todoLists must be an array");
          }
          if ("dailyTasks" in data && !Array.isArray(data.dailyTasks)) {
            throw new Error("Invalid JSON: dailyTasks must be an array");
          }
          const { tasks, todoLists, dailyTasks } = importData({
            ...data,
            dailyTasks: data.dailyTasks || [], // Default to empty array if missing
          });
          if (process.env.NODE_ENV === "development") {
            console.log("Import result:", { tasks, todoLists, dailyTasks });
          }
          if (typeof taskDispatch !== "function") {
            throw new Error("taskDispatch is not a function");
          }
          if (typeof todoDispatch !== "function") {
            throw new Error("todoDispatch is not a function");
          }
          taskDispatch({ type: "SET_STATE", tasks, dailyTasks });
          todoDispatch({ type: "SET_LISTS", lists: todoLists });
        } catch (error) {
          console.error(
            "Error in handleImportData:",
            error.message,
            error.stack
          );
          alert(
            `Failed to import data: ${error.message}. Please ensure the file is a valid JSON with tasks, todoLists, and settings.`
          );
        }
      };
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        alert("Error reading file. Please try another file.");
      };
      reader.readAsText(file);
    },
    [taskDispatch, todoDispatch, importData]
  );

  return handleImportData;
}
